// components/inventoryManagement/ListInventory.js
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Card,
  CardContent,
  Badge,
  Grid,
  Switch,
  FormControlLabel,
  Tooltip,
  Chip,
  Alert,
  Stack,
  Divider
} from "@mui/material";
import {
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from "@mui/icons-material";
import Header from "../../components/layout/Header";
import Sidebar from "../../components/layout/Sidebar";
import { fetchItems, getAllCategories } from "../../redux/actions/InventoryAction";
import CategoryTable from "../../components/inventoryManagement/CategoryTable";
import Loader from "../../components/layout/Loader";

const ListInventory = ({ nav }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);
  const [showUsage, setShowUsage] = useState(false);

  const { userInfo } = useSelector((state) => state.userSignIn);
  const { data, loading: itemsLoading, error: itemsError } = useSelector(
    (state) => state.inventorytabledata
  );
  const { categories, loading: categoriesLoading, error: categoriesError } = useSelector(
    (state) => state.getCategories
  );

  useEffect(() => {
    if (!userInfo) {
      navigate("/sign-in");
    } else {
      dispatch(fetchItems(userInfo.token));
      dispatch(getAllCategories(userInfo.token));
    }
    window.scrollTo(0, 0);
  }, [userInfo, navigate, dispatch]);

  // Map the category field to the correct name using fetched categories
  const mappedData = data?.map((categoryItem) => {
    const categoryMatch = categories?.find((cat) => cat.value === categoryItem.category);
    return {
      ...categoryItem,
      category: categoryMatch ? categoryMatch.name : categoryItem.category || "Components",
    };
  });

  // Calculate category statistics
  const getCategoryStats = (categoryData) => {
    const totalItems = categoryData.items.length;
    const lowStockItems = categoryData.items.filter(
      item => item.remainingInStock <= item.stockAlert
    ).length;
    const outOfStockItems = categoryData.items.filter(
      item => item.remainingInStock === 0
    ).length;
    const totalValue = categoryData.items.reduce(
      (sum, item) => sum + (item.remainingInStock * (item.unitPrice || 0)), 0
    );

    return {
      totalItems,
      lowStockItems,
      outOfStockItems,
      totalValue,
      healthyItems: totalItems - lowStockItems
    };
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleUsageToggle = (event) => {
    setShowUsage(event.target.checked);
  };

  if (itemsError || categoriesError) {
    return (
      <div className="text-center text-danger">
        {itemsError || categoriesError}
      </div>
    );
  }

  return (
    <>
      {userInfo && (
        <div className={nav.isSidebar}>
          <Header nav={nav} />
          <Sidebar nav={nav} />
          {/* Start content */}
          <main className="page-content">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0 p-0">
                <li className="breadcrumb-item">
                  Inventory Management
                </li>
                <li className="breadcrumb-item">
                  <Link to="/">
                    <i className="bx bx-home-alt" />
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  View Inventory Item
                </li>
              </ol>
            </nav>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pt: 3, mb: 3 }}>
              <InventoryIcon sx={{ color: 'white', fontSize: 32 }} />
              <Typography variant="h4" component="h1" sx={{ color: 'white', fontWeight: 'bold' }}>
                Inventory Management
              </Typography>
            </Box>

            {itemsLoading || categoriesLoading ? (
              <div className="text-center">
                <Loader />
              </div>
            ) : mappedData?.length > 0 ? (
              <Box sx={{ width: '100%' }}>
                {/* Summary Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={3}>
                    <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <CategoryIcon sx={{ fontSize: 40 }} />
                          <Box>
                            <Typography variant="h4" component="div">
                              {mappedData.length}
                            </Typography>
                            <Typography variant="body2">
                              Total Categories
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <CheckCircleIcon sx={{ fontSize: 40 }} />
                          <Box>
                            <Typography variant="h4" component="div">
                              {mappedData.reduce((sum, cat) => sum + getCategoryStats(cat).healthyItems, 0)}
                            </Typography>
                            <Typography variant="body2">
                              Healthy Stock
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card sx={{ bgcolor: '#FFF8E1', border: '1px solid #FFB74D', color: '#E65100' }}>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <TrendingDownIcon sx={{ fontSize: 40, color: '#FF9800' }} />
                          <Box>
                            <Typography variant="h4" component="div">
                              {mappedData.reduce((sum, cat) => sum + getCategoryStats(cat).lowStockItems, 0)}
                            </Typography>
                            <Typography variant="body2">
                              Needs Attention
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card sx={{ bgcolor: '#FCE4EC', border: '1px solid #F8BBD9', color: '#C2185B' }}>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <InventoryIcon sx={{ fontSize: 40, color: '#E91E63' }} />
                          <Box>
                            <Typography variant="h4" component="div">
                              {mappedData.reduce((sum, cat) => sum + getCategoryStats(cat).outOfStockItems, 0)}
                            </Typography>
                            <Typography variant="body2">
                              Reorder Soon
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Control Panel */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" component="div">
                        Inventory Categories
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={showUsage}
                            onChange={handleUsageToggle}
                            color="primary"
                          />
                        }
                        label={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {showUsage ? <TrendingUpIcon /> : <TrendingDownIcon />}
                            <Typography variant="body2">
                              Show {showUsage ? 'Usage' : 'Remaining'} %
                            </Typography>
                          </Stack>
                        }
                      />
                    </Stack>
                  </CardContent>
                </Card>

                {/* Tabs */}
                <Card>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                      value={activeTab}
                      onChange={handleTabChange}
                      variant="scrollable"
                      scrollButtons="auto"
                      aria-label="inventory categories"
                    >
                      {mappedData.map((category, index) => {
                        const stats = getCategoryStats(category);
                        return (
                          <Tab
                            key={category.category}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CategoryIcon />
                                <Typography variant="subtitle2">
                                  {category.category}
                                </Typography>
                                <Badge
                                  badgeContent={stats.totalItems}
                                  color="primary"
                                  sx={{ ml: 1 }}
                                />
                                {stats.lowStockItems > 0 && (
                                  <Tooltip title={`${stats.lowStockItems} items need restocking`}>
                                    <Badge
                                      badgeContent={stats.lowStockItems}
                                      sx={{
                                        '& .MuiBadge-badge': {
                                          bgcolor: '#FF9800',
                                          color: 'white'
                                        }
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            }
                            sx={{ textTransform: 'none', minWidth: 'auto' }}
                          />
                        );
                      })}
                    </Tabs>
                  </Box>

                  {/* Tab Content */}
                  <Box sx={{ p: 3 }}>
                    {mappedData.map((category, index) => (
                      <div
                        key={category.category}
                        role="tabpanel"
                        hidden={activeTab !== index}
                        style={{ display: activeTab === index ? 'block' : 'none' }}
                      >
                        {activeTab === index && (
                          <Box>
                            {/* Category Header with Stats */}
                            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                              <Chip
                                icon={<InventoryIcon />}
                                label={`${getCategoryStats(category).totalItems} Total Items`}
                                color="primary"
                                variant="outlined"
                              />
                              <Chip
                                icon={<CheckCircleIcon />}
                                label={`${getCategoryStats(category).healthyItems} Healthy`}
                                color="success"
                                variant="outlined"
                              />
                              {getCategoryStats(category).lowStockItems > 0 && (
                                <Chip
                                  icon={<TrendingUpIcon />}
                                  label={`${getCategoryStats(category).lowStockItems} Need Attention`}
                                  sx={{ 
                                    bgcolor: '#FFF8E1', 
                                    color: '#E65100',
                                    border: '1px solid #FFB74D'
                                  }}
                                  variant="outlined"
                                />
                              )}
                              {getCategoryStats(category).outOfStockItems > 0 && (
                                <Chip
                                  icon={<InventoryIcon />}
                                  label={`${getCategoryStats(category).outOfStockItems} Reorder Soon`}
                                  sx={{ 
                                    bgcolor: '#FCE4EC', 
                                    color: '#C2185B',
                                    border: '1px solid #F8BBD9'
                                  }}
                                  variant="outlined"
                                />
                              )}
                            </Stack>

                            {/* Gentle reminder for low stock items */}
                            {getCategoryStats(category).lowStockItems > 0 && (
                              <Alert 
                                severity="info" 
                                sx={{ 
                                  mb: 3,
                                  bgcolor: '#E3F2FD',
                                  border: '1px solid #90CAF9'
                                }}
                              >
                                <Typography variant="body2">
                                  💡 <strong>{getCategoryStats(category).lowStockItems}</strong> items in this category might need restocking soon. Consider reviewing your inventory levels.
                                </Typography>
                              </Alert>
                            )}

                            {/* Category Table */}
                            <CategoryTable item={category} showUsage={showUsage} />
                          </Box>
                        )}
                      </div>
                    ))}
                  </Box>
                </Card>
              </Box>
            ) : (
              <Card>
                <CardContent>
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <InventoryIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                      No inventory items found
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Start by adding some inventory items to your system.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </main>
        </div>
      )}
    </>
  );
};

export default ListInventory;